import { Copy, CornerDownRight, Package } from 'lucide-react';
import { copyToClipboard } from '@/lib/copyToClipboard';
import { formatDate, formatValue } from '@/lib/formatters';
import { getShipmentPipelineStageKey } from '@/features/Shipment/model/shipmentPipeline';
import { MouseEvent } from 'react';
import { ShipmentListCardProps } from './types';
import { ShipmentProgressLine } from '@/components/ShipmentProgressLine';
import { toast } from '@/components/ui/sonner';

const ShipmentListCard = (props: ShipmentListCardProps) => {
  const {
    item,
    onOpenShipment
  } = props;

  const displayDate = formatDate({ value: item.displayDate, variant: 'date' });

  const handleCardClick = (): void => {
    onOpenShipment(item.id);
  };

  const handleCopyShipmentId = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.stopPropagation();

    if (!item.shipmentNumber) return;

    try {
      await copyToClipboard(item.shipmentNumber);
      toast.success(`Скопировано ${item.shipmentNumber}`);
    } catch {
      return;
    }
  };

  return (
    <article
      className="group flex cursor-pointer flex-col overflow-hidden rounded-[16px] border border-border-strong bg-surface transition-colors hover:bg-[linear-gradient(90deg,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.08)_100%),linear-gradient(90deg,hsl(var(--surface))_0%,hsl(var(--surface))_100%)] active:bg-accent-soft"
      data-shipment-id={item.id}
      onClick={handleCardClick}
    >
      <div className="flex gap-2 border-b border-border-strong px-4 pb-2 pt-4">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-[8px] border border-border bg-accent-soft text-link">
          <Package
            className="size-4"
            strokeWidth={1.75}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-1 whitespace-nowrap">
            <p className="truncate font-sans text-sm font-semibold leading-5 text-foreground">
              {item.number}
            </p>
            <span className="shrink-0 font-sans text-xs leading-4 text-muted-foreground">
              ·
            </span>
            <p className="shrink-0 font-sans text-xs leading-4 text-muted-foreground">
              {displayDate}
            </p>
          </div>
          <p className="truncate font-sans text-xs leading-4 text-muted-foreground">
            {item.supplierName}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 py-2">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xs leading-4 text-muted-foreground-soft">
            Статус поставки
          </p>
          <div className="flex flex-col gap-1">
            <p className="truncate font-sans text-sm font-semibold leading-5 text-accent">
              {item.statusLabel}
            </p>
            {item.nextStatusLabel && (
              <div className="flex min-w-0 items-center gap-1">
                <CornerDownRight
                  className="size-4 shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                />
                <p className="truncate font-sans text-xs leading-4 text-muted-foreground">
                  {item.nextStatusLabel}
                </p>
              </div>
            )}
          </div>
        </div>

        <ShipmentProgressLine
          activeStageKey={getShipmentPipelineStageKey(item.status)}
          className="mt-0"
          variant="shipmentsList"
        />
      </div>

      <div className="flex items-center gap-1 border-t border-border-strong px-4 pb-4 pt-2">
        <p className="min-w-0 truncate font-sans text-xs leading-4 text-muted-foreground">
          ID поставки: {formatValue({ value: item.shipmentNumber })}
        </p>
        <button
          aria-label="Скопировать ID поставки"
          className="flex size-5 shrink-0 items-center justify-center rounded-[4px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          disabled={!item.shipmentNumber}
          onClick={handleCopyShipmentId}
          type="button"
        >
          <Copy
            className="size-4"
            strokeWidth={1.75}
          />
        </button>
      </div>
    </article>
  );
};

export default ShipmentListCard;